import { Button, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { PiMegaphone } from 'react-icons/pi';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { formatDateToDateDisplay } from '@/utils/formatDateToDateDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { FundraisingCampaign, FundraisingCampaignStatus } from '@/model';
import { formatCurrency } from '@/utils';
import { useTranslation } from 'react-i18next';

export interface FundraisingCampaignTableProps {
  data: FundraisingCampaign[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: FundraisingCampaign) => void;
  onDeleteClick: (item: FundraisingCampaign) => void;
  onPublishClick: (item: FundraisingCampaign) => void;
  onEndClick: (item: FundraisingCampaign) => void;
  onJustifyExpensesClick: (item: FundraisingCampaign) => void;
}

export const FundraisingCampaignTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
  onEndClick,
  onPublishClick,
  onJustifyExpensesClick,
}: FundraisingCampaignTableProps) => {
  const { t } = useTranslation();
  const textByStatus = {
    [FundraisingCampaignStatus.NOT_PUBLISHED]: t(
      'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_status_not_published',
    ),
    [FundraisingCampaignStatus.PUBLISHED]: t(
      'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_status_published',
    ),
    [FundraisingCampaignStatus.SCHEDULED]: t(
      'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_status_scheduled',
    ),
    [FundraisingCampaignStatus.ENDED]: t(
      'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_status_ended',
    ),
  };

  const columnHelper = createColumnHelper<{
    id: string;
    created_at: string;
    title: string;
    start_at: string;
    end_at: string;
    goal: number;
    collected: number;
    status: FundraisingCampaignStatus;
  }>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_created_at_header'),
    }),
    columnHelper.accessor('title', {
      cell: props => props.getValue(),
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_campaign_header'),
    }),
    columnHelper.display({
      id: 'period',
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_period_header'),
      cell: props => {
        const hasPeriod = !!props.row.original.start_at;

        if (!hasPeriod)
          return t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_period_empty');

        const startAt = formatDateToDateDisplay(props.row.original.start_at);
        const endAt = formatDateToDateDisplay(props.row.original.end_at);
        return (
          <>
            {startAt} a {endAt}
          </>
        );
      },
      enableSorting: false,
      meta: {
        align: 'left',
      },
    }),
    columnHelper.accessor('goal', {
      cell: props => formatCurrency(props.getValue()),
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_goal_header'),
    }),
    columnHelper.accessor('collected', {
      cell: props => {
        const hasCollectedValue = !!props.getValue();

        if (!hasCollectedValue)
          return t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_collected_empty');

        const hasReachedGoal = props.row.original.collected >= props.row.original.goal;
        const color = hasReachedGoal ? 'green' : 'red';

        return <Text color={`${color}.500`}>{formatCurrency(props.getValue())}</Text>;
      },
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_collected_header'),
    }),
    columnHelper.accessor('status', {
      cell: props => textByStatus[props.getValue()],
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_status_header'),
    }),
    columnHelper.display({
      id: 'justify-expenses',
      header: '',
      cell: props => {
        const hasEnded = props.row.original.status === FundraisingCampaignStatus.ENDED;

        if (!hasEnded) return '';

        return (
          <Button
            variant='link'
            color='brand.500'
            textDecor='underline'
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => onJustifyExpensesClick?.(props.row.original as FundraisingCampaign)}
          >
            {t(
              'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_justify_expenses_button',
            )}
          </Button>
        );
      },
      enableSorting: false,
      meta: {
        align: 'left',
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_actions_header'),
      cell: props => {
        const isCampaignPublished = props.row.original.status === FundraisingCampaignStatus.PUBLISHED;
        const isCampaignNotPublished = props.row.original.status === FundraisingCampaignStatus.NOT_PUBLISHED;

        return (
          <Flex justifyContent={'end'}>
            <Menu>
              <MenuButton
                textColor='text.table'
                as={IconButton}
                variant='ghost'
                size='sm'
                icon={<Icon as={EllipsisVerticalIcon} />}
                aria-label={t(
                  'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_more_actions_label',
                )}
              />
              <MenuList textColor='white'>
                <MenuItem
                  onClick={() => onEditClick?.(props.row.original as FundraisingCampaign)}
                  icon={
                    <Text>
                      <ChakraFeatherIcon name='edit-2' w='18px' />
                    </Text>
                  }
                >
                  <Text>
                    {t('pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_edit_action')}
                  </Text>
                </MenuItem>

                {isCampaignPublished ? (
                  <MenuItem
                    onClick={() => onEndClick?.(props.row.original as FundraisingCampaign)}
                    icon={
                      <Text>
                        <ChakraFeatherIcon name='x-circle' w='18px' />
                      </Text>
                    }
                  >
                    <Text>
                      {t(
                        'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_end_action',
                      )}
                    </Text>
                  </MenuItem>
                ) : null}

                {isCampaignNotPublished ? (
                  <MenuItem
                    onClick={() => onPublishClick?.(props.row.original as FundraisingCampaign)}
                    icon={
                      <Text>
                        <Icon as={PiMegaphone} w='18px' transform='scaleX(-1)' />
                      </Text>
                    }
                  >
                    <Text>
                      {t(
                        'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_publish_action',
                      )}
                    </Text>
                  </MenuItem>
                ) : null}

                <MenuItem
                  onClick={() => onDeleteClick?.(props.row.original as FundraisingCampaign)}
                  icon={
                    <Text color='red.500'>
                      <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                    </Text>
                  }
                >
                  <Text color='red.500'>
                    {t(
                      'pages.fundraising_campaign.components.fundraising_campaign_table.fundraising_campaign_table_delete_action',
                    )}
                  </Text>
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        );
      },
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
